import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * IT Related Action Items (support ticket) reporting.
 * Replaces the former InvGate ITSM feed. Sources entirely from the configured
 * IT Support board/department (resolved server-side from Module Template Config)
 * and SLA tracking — no values are hardcoded.
 */
const ITActionItemsSummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const totalRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line

  const syncHeights = () => {
    if (!topRef.current || !totalRef.current) return;
    const isPrinting = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('print').matches;
    if (isPrinting) { totalRef.current.style.height = 'auto'; return; }
    requestAnimationFrame(() => {
      const h = topRef.current?.offsetHeight || 0;
      if (h > 0) totalRef.current.style.height = h + 'px';
    });
  };

  useEffect(() => { syncHeights(); }, [summary?.topRequesters]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia ? window.matchMedia('print') : null;
    const handleChange = () => {
      if (mq && mq.matches) { if (totalRef.current) totalRef.current.style.height = 'auto'; }
      else syncHeights();
    };
    if (mq) mq.addEventListener('change', handleChange);
    window.addEventListener('beforeprint', handleChange);
    window.addEventListener('afterprint', handleChange);
    return () => {
      if (mq) mq.removeEventListener('change', handleChange);
      window.removeEventListener('beforeprint', handleChange);
      window.removeEventListener('afterprint', handleChange);
    };
  }, []);

  async function fetchSummary() {
    setLoading(true); setError('');
    try {
      const response = await apiFetch('/api/itactionitems/summary');
      if (!response.ok) throw new Error('Failed to fetch IT Action Items summary');
      const data = await response.json();
      setSummary(data);
    }
    catch (e) {
      Sentry.captureException(e);
      setError(e.message); setSummary(null);
    }
    finally { setLoading(false); }
  }

  const prevRange = useMemo(() => {
    if (summary?.previousMonthRange?.start && summary?.previousMonthRange?.endExclusive)
      return { start: new Date(summary.previousMonthRange.start), end: new Date(summary.previousMonthRange.endExclusive) };
    const now = new Date();
    const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = new Date(firstThis.getFullYear(), firstThis.getMonth() - 1, 1);
    const end = new Date(firstThis.getFullYear(), firstThis.getMonth(), 1);
    return { start, end };
  }, [summary]);

  const daysInPrevMonth = useMemo(() => {
    if (!prevRange?.start) return 30;
    const y = prevRange.start.getUTCFullYear();
    const m = prevRange.start.getUTCMonth();
    return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  }, [prevRange]);

  const dailyData = useMemo(() => {
    const counts = Array.from({ length: daysInPrevMonth }, (_, idx) => ({ day: idx + 1, tickets: 0 }));
    if (Array.isArray(summary?.previousMonthDailyCounts)) {
      summary.previousMonthDailyCounts.forEach(x => {
        const di = (x.day ?? 0) - 1; if (di >= 0 && di < counts.length) counts[di].tickets = x.count ?? 0;
      });
    }
    return counts;
  }, [summary, daysInPrevMonth]);

  const statusData = useMemo(() =>
    Object.entries(summary?.statusCounts || {}).map(([status, count]) => ({ status, count })), [summary]);
  const priorityData = useMemo(() =>
    Object.entries(summary?.priorityCounts || {}).map(([priority, count]) => ({ priority, count })), [summary]);
  const categoryData = useMemo(() =>
    Object.entries(summary?.categoryCounts || {})
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10), [summary]);

  const ageingData = useMemo(() => ([
    { bucket: '0-2 days', count: summary?.ageing0To2Days ?? 0 },
    { bucket: '3-7 days', count: summary?.ageing3To7Days ?? 0 },
    { bucket: '8-30 days', count: summary?.ageing8To30Days ?? 0 },
    { bucket: '30+ days', count: summary?.ageingOver30Days ?? 0 },
  ]), [summary]);

  const topRequesters = useMemo(() =>
    Array.isArray(summary?.topRequesters) ? summary.topRequesters : [], [summary]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 80) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 60) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 40) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 20) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const fmtMins = (m) => {
    if (m == null) return '—';
    if (m < 60) return `${Math.round(m)} min`;
    if (m < 60 * 24) return `${(m / 60).toFixed(1)} hrs`;
    return `${(m / (60 * 24)).toFixed(1)} days`;
  };

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!summary) return null;

  // Configuration gap — surface clearly so it can be fixed in Module Template Config
  if (!summary.isConfigured) {
    return (
      <div className="dashboard-card group-wrapper it-tickets-summary">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>IT Related Action Items</h2>
        <div className="dashboard-card exec-banner" style={{ borderLeft: '4px solid #d32f2f' }}>
          <div className="exec-banner-text">
            <strong style={{ color: '#d32f2f' }}>⚠ Configuration gap:</strong> No IT Support board is assigned in
            <em> Module Template Configuration</em>. Assign a Default Board to the IT module&apos;s Support Ticket
            context so support tickets can be tracked and reported here.
          </div>
        </div>
      </div>
    );
  }

  const prevMonthLabel = prevRange ? prevRange.start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'Previous Month';
  const total = summary.totalTickets ?? 0;
  const active = summary.activeTickets ?? 0;
  const closureRate = summary.closureRate ?? 0;
  const resolutionSla = summary.slaResolutionCompliance ?? 0;
  const responseSla = summary.slaResponseCompliance ?? 0;
  const healthScore = summary.healthScore ?? null;
  const scoreInterp = getScoreInterpretation(healthScore);
  const chartMargin = { left: 0, right: 8, top: 5, bottom: 5 };

  const llmProxyText = [
    `IT Support Health: ${healthScore ?? '—'}/100 on board "${summary.boardName ?? 'Unknown'}" (${summary.departmentName ?? 'IT'})`,
    `Volumes (${prevMonthLabel}): total ${total}, active ${active}, created ${summary.createdInPeriod ?? 0}, resolved ${summary.resolvedInPeriod ?? 0}`,
    `SLA: response ${responseSla.toFixed(0)}% met, resolution ${resolutionSla.toFixed(0)}% met (${summary.slaResolutionBreached ?? 0} breached)`,
    `Ageing: ${summary.ageingOver30Days ?? 0} tickets over 30 days; oldest ${summary.oldestActiveTicketDays ?? 0} days`,
    `Attention: ${summary.unassignedActiveTickets ?? 0} unassigned, ${summary.escalatedActiveTickets ?? 0} escalated, ${summary.needsAttentionTickets ?? 0} flagged`,
    topRequesters.length ? `Top reporters: ${topRequesters.slice(0, 3).map(x => `${x.name} ${x.count}`).join(', ')}` : null
  ].filter(Boolean).join('\n');

  return (
    <div className="dashboard-card group-wrapper it-tickets-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>IT Related Action Items</h2>
      <div style={{ fontSize: 12, color: '#777', marginBottom: 12 }}>
        Board: <strong>{summary.boardName || '—'}</strong> · Department: <strong>{summary.departmentName || '—'}</strong>
      </div>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Support Health Score</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> The IT team resolved <strong>{summary.resolvedInPeriod ?? 0}</strong> of
              {' '}<strong>{summary.createdInPeriod ?? 0}</strong> tickets logged in {prevMonthLabel}, with
              {' '}<strong>{resolutionSla.toFixed(0)}%</strong> meeting their resolution SLA.
              {resolutionSla >= 85 && ' SLA performance is strong.'}
              {resolutionSla >= 60 && resolutionSla < 85 && ' SLA performance is acceptable but has room to improve.'}
              {resolutionSla < 60 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Resolution SLA compliance is low — review staffing and prioritisation.</span>}
              {(summary.ageingOver30Days ?? 0) > 0 && <span style={{ color: '#ff5722', fontWeight: 600 }}> {summary.ageingOver30Days} ticket(s) have been open over 30 days.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div ref={totalRef} className="dashboard-card metric-card" title="Tickets currently tracked on the IT Support board">
          <div className="metric-label">Total Tickets</div>
          <div className="metric-value">{total}</div>
          <div className="metric-subtitle">{active} active</div>
        </div>
        <div className="dashboard-card metric-card metric-card--md" title="Tickets resolved/closed across the board">
          <div className="metric-label">Closure Rate</div>
          <div className="metric-value" style={{ color: closureRate >= 80 ? '#2e7d32' : closureRate >= 60 ? '#ffa000' : '#d32f2f' }}>{closureRate.toFixed(0)}%</div>
          <div className="metric-subtitle">{summary.resolvedInPeriod ?? 0} resolved in {prevMonthLabel}</div>
        </div>
        <div className="dashboard-card metric-card metric-card--md" title="Tickets that met their resolution SLA target">
          <div className="metric-label">Resolution SLA</div>
          <div className="metric-value" style={{ color: resolutionSla >= 85 ? '#2e7d32' : resolutionSla >= 60 ? '#ffa000' : '#d32f2f' }}>{resolutionSla.toFixed(0)}%</div>
          <div className="metric-subtitle">{summary.slaResolutionBreached ?? 0} breached · avg {fmtMins(summary.avgResolutionMinutes)}</div>
        </div>
        <div className="dashboard-card metric-card metric-card--md" title="First-response SLA compliance">
          <div className="metric-label">Response SLA</div>
          <div className="metric-value" style={{ color: responseSla >= 85 ? '#2e7d32' : responseSla >= 60 ? '#ffa000' : '#d32f2f' }}>{responseSla.toFixed(0)}%</div>
          <div className="metric-subtitle">{summary.slaResponseBreached ?? 0} breached · avg {fmtMins(summary.avgResponseMinutes)}</div>
        </div>
        <div ref={topRef} className="dashboard-card metric-card metric-card--fill" title="Users who logged the most tickets - may indicate training needs">
          <div className="metric-label" style={{ marginBottom: 8 }}>Top Issue Reporters</div>
          {topRequesters.length === 0 ? (
            <div style={{ fontSize: 10, color: '#777', textAlign: 'center' }}>No data</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
              {topRequesters.slice(0, 5).map(c => (
                <span key={c.requesterId} className="inline-tag" style={{ background: '#f6304015' }}>
                  {c.name}: {c.count}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Attention signals */}
      {((summary.unassignedActiveTickets ?? 0) > 0 || (summary.escalatedActiveTickets ?? 0) > 0 || (summary.needsAttentionTickets ?? 0) > 0) && (
        <div className="metrics-row">
          <div className="dashboard-card metric-card metric-card--md" title="Active tickets with no assignee">
            <div className="metric-label">Unassigned</div>
            <div className="metric-value" style={{ color: (summary.unassignedActiveTickets ?? 0) > 0 ? '#ff5722' : '#2e7d32' }}>{summary.unassignedActiveTickets ?? 0}</div>
          </div>
          <div className="dashboard-card metric-card metric-card--md" title="Active tickets that have been escalated">
            <div className="metric-label">Escalated</div>
            <div className="metric-value" style={{ color: (summary.escalatedActiveTickets ?? 0) > 0 ? '#ff5722' : '#2e7d32' }}>{summary.escalatedActiveTickets ?? 0}</div>
          </div>
          <div className="dashboard-card metric-card metric-card--md" title="Tickets explicitly flagged as needing attention">
            <div className="metric-label">Needs Attention</div>
            <div className="metric-value" style={{ color: (summary.needsAttentionTickets ?? 0) > 0 ? '#ff5722' : '#2e7d32' }}>{summary.needsAttentionTickets ?? 0}</div>
          </div>
          <div className="dashboard-card metric-card metric-card--md" title="Age of the oldest currently-open ticket">
            <div className="metric-label">Oldest Open</div>
            <div className="metric-value" style={{ color: (summary.oldestActiveTicketDays ?? 0) > 30 ? '#d32f2f' : (summary.oldestActiveTicketDays ?? 0) > 7 ? '#ffa000' : '#2e7d32' }}>{summary.oldestActiveTicketDays ?? 0}<span style={{ fontSize: 12 }}> days</span></div>
          </div>
        </div>
      )}

      {/* Tickets per Day */}
      <div className="dashboard-card">
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Daily Ticket Volume ({prevMonthLabel})</h4>
        <ResponsiveContainer width={isPrintMode ? 680 : "100%"} height={170} minHeight={170}>
          <BarChart data={dailyData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" interval={0} tick={{ fontSize: 8 }} label={{ value: 'Day of Month', position: 'insideBottom', offset: -5 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 8 }} />
            <Tooltip />
            <Bar dataKey="tickets" fill="#f63040" radius={[2, 2, 2, 2]} isAnimationActive={!isPrintMode} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Status and Priority */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card" style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Tickets by Status</h4>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={140} minHeight={140}>
            <BarChart data={statusData} layout="vertical" margin={chartMargin}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 8 }} />
              <YAxis dataKey="status" type="category" width={110} tick={{ fontSize: 8 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#4e73df" radius={[2, 2, 2, 2]} isAnimationActive={!isPrintMode} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="dashboard-card" style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Tickets by Priority</h4>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={140} minHeight={140}>
            <BarChart data={priorityData} layout="vertical" margin={chartMargin}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 8 }} />
              <YAxis dataKey="priority" type="category" width={110} tick={{ fontSize: 8 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#ffa000" radius={[2, 2, 2, 2]} isAnimationActive={!isPrintMode} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ageing and Category */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card" style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Active Ticket Ageing</h4>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={140} minHeight={140}>
            <BarChart data={ageingData} layout="vertical" margin={chartMargin}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 8 }} />
              <YAxis dataKey="bucket" type="category" width={110} tick={{ fontSize: 8 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8e24aa" radius={[2, 2, 2, 2]} isAnimationActive={!isPrintMode} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {categoryData.length > 0 && (
          <div className="dashboard-card" style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Tickets by Category</h4>
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={140} minHeight={140}>
              <BarChart data={categoryData} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 8 }} />
                <YAxis dataKey="category" type="category" width={110} tick={{ fontSize: 8 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#43a047" radius={[2, 2, 2, 2]} isAnimationActive={!isPrintMode} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding IT Support Metrics:</strong>
        <ul>
          <li><strong>Support Health Score:</strong> Combines resolution SLA compliance (40%), throughput/closure (35%) and backlog freshness (25%). Scores above 60 indicate healthy operations.</li>
          <li><strong>Resolution SLA:</strong> Percentage of tracked tickets resolved within target. Breaches and ageing backlog drag this down.</li>
          <li><strong>Ageing:</strong> Tickets open over 30 days represent stuck work that needs escalation or closure.</li>
          <li><strong>Attention Signals:</strong> Unassigned, escalated and flagged tickets are the items most at risk of breaching SLA.</li>
          <li><strong>Top Reporters:</strong> Users logging many tickets may need targeted training or have persistent underlying issues.</li>
        </ul>
      </div>
    </div>
  );
};

export default ITActionItemsSummary;
